import React, { useState } from 'react';
import { Link, ButtonGroup, PrimaryButton } from '@common/components';
import { StaffingRequestResubmitButton } from 'works/pages/supplier/staffing-requests/staffing-request/staffing-request-resubmit-button';
import { StaffingRequestUpdateSubmissionDrawer } from '@applicant-flow/components';
import { Button } from 'antd';
import { StaffingRequestApplicationsType } from '@applicant-flow/graphql/queries/staffing-request-applications';
import { buildDialogUrl } from '@common/services';
import { ViewSubmissionDrawerButton } from './view-submission-drawer-button';
import { StaffingRequestTypeType } from '@applicant-flow/types/staffing-request';

export function ActiveCtaButton({
  staffingRequestId,
  staffingRequestType,
  application,
}: {
  staffingRequestId: string;
  staffingRequestType?: StaffingRequestTypeType;
  application: StaffingRequestApplicationsType;
}) {
  const { worksProfile } = application;

  const [showUpdateSubmissionDrawer, setShowUpdateSubmissionDrawer] =
    useState(false);

  if (application.state === 'submittal_packet_change_requested') {
    return (
      <>
        <PrimaryButton onClick={() => setShowUpdateSubmissionDrawer(true)}>
          Update
        </PrimaryButton>
        <StaffingRequestUpdateSubmissionDrawer
          visible={showUpdateSubmissionDrawer}
          onClose={() => setShowUpdateSubmissionDrawer(false)}
          staffingRequestApplicationId={application.id}
        />
      </>
    );
  }

  // SUBMITTED
  const reviewStates = ['submitted', 'in_review', 'rejected'];
  if (reviewStates.includes(application.state)) {
    return <ViewSubmissionDrawerButton applicationId={application.id} />;
  }

  const offerConfirmationTrigger =
    application.activeOffer &&
    ['sent', 'change_requested', 'rejected'].includes(
      application.activeOffer.state
    ) ? (
      <Button>View Offer</Button>
    ) : (
      <PrimaryButton>Review Offer</PrimaryButton>
    );

  // OFFER
  if (application.state === 'offer') {
    if (!application.activeOffer || !offerConfirmationTrigger) {
      return <ViewSubmissionDrawerButton applicationId={application.id} />;
    }

    const offerConfirmationDrawerUrl = buildOfferConfirmationDrawerUrl(
      application.activeOffer?.id
    );

    return (
      <ButtonGroup>
        <Link to={offerConfirmationDrawerUrl}>{offerConfirmationTrigger}</Link>
        <ViewSubmissionDrawerButton applicationId={application.id} />
      </ButtonGroup>
    );
  }

  // ACCEPTED
  if (application.state === 'accepted') {
    const activeOffer = application.activeOffer;

    const offerConfirmationDrawerUrl = buildOfferConfirmationDrawerUrl(
      activeOffer?.id
    );

    return (
      <ButtonGroup>
        {activeOffer && (
          <>
            <Link
              to={`/works/team/${activeOffer.staffingRequestAssignment?.id}`}
            >
              <PrimaryButton>Assignment</PrimaryButton>
            </Link>
            <Link to={offerConfirmationDrawerUrl}>
              <Button>View Offer</Button>
            </Link>
          </>
        )}
        <ViewSubmissionDrawerButton applicationId={application.id} />
      </ButtonGroup>
    );
  }

  // WITHDRAWN
  if (application.state === 'withdrawn') {
    if (!application.canResubmit.value) return null;

    return (
      application.canResubmit.value && (
        <ButtonGroup>
          <StaffingRequestResubmitButton
            staffingRequestId={staffingRequestId}
            staffingRequestType={staffingRequestType}
            selectedWorksProfile={worksProfile}
            applicationId={application.id}
          >
            Resubmit
          </StaffingRequestResubmitButton>
          <ViewSubmissionDrawerButton applicationId={application.id} />
        </ButtonGroup>
      )
    );
  }

  return null;
}

function buildOfferConfirmationDrawerUrl(offerId?: string) {
  if (!offerId) return '';

  return buildDialogUrl('offer-confirmation-drawer', {
    props: {
      offerId,
    },
  });
}
